import React, { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { subscribeToPushNotifications, checkSubscriptionStatus } from '../../utils/pushNotificationHelper';
import './NotificationPrompt.css';

const NotificationPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAndShowPrompt = async () => {
      try {
        // Only show for students
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (user.role !== 'student') {
          setIsChecking(false);
          return;
        }

        // On web, check browser support
        if (!Capacitor.isNativePlatform()) {
          if (!('Notification' in window) || !('serviceWorker' in navigator)) {
            console.log('Notifications not supported');
            setIsChecking(false);
            return;
          }
          if (Notification.permission === 'denied') {
            setIsChecking(false);
            return;
          }
        }

        // Check if user dismissed the prompt recently
        const dismissedUntil = localStorage.getItem('notificationPromptDismissedUntil');
        if (dismissedUntil) {
          const dismissedDate = new Date(dismissedUntil);
          if (dismissedDate > new Date()) {
            setIsChecking(false);
            return;
          } else {
            // Clear expired dismissal
            localStorage.removeItem('notificationPromptDismissedUntil');
          }
        }

        // Check current subscription status
        const status = await checkSubscriptionStatus();
        
        // If already subscribed, don't show prompt
        if (status.subscribed) {
          console.log('✅ User already subscribed to notifications');
          setIsChecking(false);
          return;
        }

        // On web: if permission already granted but not subscribed, auto-subscribe
        if (!Capacitor.isNativePlatform() && Notification.permission === 'granted') {
          console.log('Permission granted, auto-subscribing...');
          const result = await subscribeToPushNotifications();
          if (result.success) {
            console.log('✅ Auto-subscribed successfully');
          }
          setIsChecking(false);
          return;
        }

        // Native apps get the OS permission dialog via initPushNotifications — skip web UI
        if (Capacitor.isNativePlatform()) {
          setIsChecking(false);
          return;
        }

        // Show prompt after a delay (5 seconds after dashboard loads)
        setTimeout(() => {
          setShowPrompt(true);
          setIsChecking(false);
        }, 5000);

      } catch (error) {
        console.error('Error checking notification status:', error);
        setIsChecking(false);
      }
    };

    checkAndShowPrompt();
  }, []);

  const handleEnable = async () => {
    setIsLoading(true);
    
    try {
      const result = await subscribeToPushNotifications();
      
      if (result.success) {
        setShowPrompt(false);
        
        // Show success toast
        showToast('✅ Notifications enabled! You will receive weekly logbook reminders.', 'success');
      } else {
        if (result.error.includes('permission denied')) {
          showToast('❌ Permission denied. Please allow notifications in your browser settings.', 'error');
        } else {
          showToast('❌ Failed to enable notifications. Please try again.', 'error');
        }
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      showToast('❌ An error occurred. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemindLater = () => {
    setShowPrompt(false);
    // Show again next session
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    
    // Don't ask again for 7 days
    const dismissedUntil = new Date();
    dismissedUntil.setDate(dismissedUntil.getDate() + 7);
    localStorage.setItem('notificationPromptDismissedUntil', dismissedUntil.toISOString());
    
    showToast('You can enable notifications anytime from your profile settings.', 'info');
  };

  const showToast = (message, type = 'info') => {
    // Create toast notification
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#2ECC71' : type === 'error' ? '#E74C3C' : '#3498DB'};
      color: white;
      padding: 16px 24px;
      border-radius: 12px;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
      z-index: 10000;
      font-size: 14px;
      font-weight: 500;
      max-width: 400px;
      animation: slideInRight_455162 0.3s ease;
    `;
    toast.textContent = message;
    document.body.appendChild(toast);

    // Remove after 5 seconds
    setTimeout(() => {
      toast.style.animation = 'slideOutRight_455162 0.3s ease';
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 5000);
  };

  if (isChecking || !showPrompt) {
    return null;
  }

  return (
    <>
      <style>{`
        @keyframes slideInRight_455162 {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideOutRight_455162 {
          from {
            opacity: 1;
            transform: translateX(0);
          }
          to {
            opacity: 0;
            transform: translateX(50px);
          }
        }
      `}</style>

      <div className="notificationPromptOverlay_455162">
        <div className="notificationPromptCard_455162">
          <div className="notificationPromptHeader_455162">
            <span className="notificationIcon1_455162">🔔</span>
            <h3>Enable Weekly Reminders</h3>
          </div>
          
          <div className="notificationPromptBody_455162">
            <p className="mainMessage_455162">
              Stay on top of your logbook entries! Get weekly reminders every <strong>Saturday at 9 AM</strong> 
              to ensure you never miss your clinical records.
            </p>
            
            <div className="notificationBenefits_455162">
              <div className="benefitItem_455162">
                <span className="benefitIcon_455162">✅</span>
                <span>Never miss a submission deadline</span>
              </div>
              <div className="benefitItem_455162">
                <span className="benefitIcon_455162">📱</span>
                <span>Get notified on all your devices</span>
              </div>
              <div className="benefitItem_455162">
                <span className="benefitIcon_455162">⏰</span>
                <span>Timely reminders every Saturday</span>
              </div>
              <div className="benefitItem_455162">
                <span className="benefitIcon_455162">🎯</span>
                <span>Keep your clinical records up-to-date</span>
              </div>
            </div>

            <div className="privacyNote_455162">
              <p>🔒 We respect your privacy. You'll only receive weekly logbook reminders.</p>
            </div>
          </div>

          <div className="notificationPromptActions_455162">
            <button 
              className="btnEnable_455162" 
              onClick={handleEnable}
              disabled={isLoading}
            >
              {isLoading ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                  <span className="spinnerSmall_455162"></span>
                  Enabling...
                </span>
              ) : (
                '🔔 Enable Notifications'
              )}
            </button>
            
            <button 
              className="btnRemindLater_455162" 
              onClick={handleRemindLater}
              disabled={isLoading}
            >
              Remind Me Later
            </button>
            
            <button 
              className="btnDismiss_455162" 
              onClick={handleDismiss}
              disabled={isLoading}
            >
              Don't Ask Again (7 days)
            </button>
          </div>

          <p className="settingsNote_455162">
            💡 You can change this anytime in your profile settings
          </p>
        </div>
      </div>
    </>
  );
};

export default NotificationPrompt;